import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { AudioLines, OctagonX, RotateCw, ScrollText, Server, Stethoscope } from "lucide-react";
import { useLatestJobResult } from "../lib/jobs.ts";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { Banner, Button, Card, Dot, Empty, ErrorState, toneColor, type Tone } from "../components/Ui.tsx";
import { JobAction } from "../components/JobAction.tsx";
import { AppIcon } from "../components/AppIcon.tsx";
import { fmtAge } from "../lib/time.ts";

/**
 * Discord bot kontrolü.
 *
 * Verilerin tamamı agent işlerinin sonucundan geliyor; ayrı REST ucu yok. Sayfa
 * açılışta gözcünün ürettiği son sonucu gösterir, "yenile" yeni iş başlatır.
 *
 * ⚠ Oynatıcı eylemleri ve restart gerçek dinleyicilerin yayınını keser — onay
 * seviyesi job tanımından gelir ("click" → iki tıklama). "Tümünü durdur" da bilerek
 * "click": arıza anında kod aramak zaman kaybettirir.
 */

interface Pm2Row {
  name: string;
  status: string;
  memoryMb: number;
  cpu: number;
  restarts: number;
  uptimeMin: number;
}

interface Diagnosis {
  healthy: boolean;
  firstFailure: string | null;
  suggestedFix: string | null;
  findings: { step: string; ok: boolean; detail: string }[];
}

interface BotState {
  bot?: string;
  guilds: {
    id: string;
    name: string;
    voiceChannel?: string;
    listeners?: number;
    playing?: string;
    author?: string;
    queueLength?: number;
    source?: string;
    paused?: boolean;
  }[];
}

function age(job: { createdAt: string } | null): string | undefined {
  return job ? `son kontrol ${fmtAge(Date.now() - new Date(job.createdAt).getTime())}` : undefined;
}

/**
 * pm2 süreç adı → Discord'daki bot. İki bot AYNI kodu farklı token ile çalıştırıyor;
 * eşleşme VPS'teki süreçlerin CLIENT_ID'sinden doğrulandı (2026-09-23):
 * raadiotr = 801486624176734248 "RAADIO TR", raadiotr2 = 1473278846454202623 "Müziking".
 */
const BOTS: Record<string, { name: string; icon: string }> = {
  raadiotr: { name: "RAADIO TR", icon: "bot-raadiotr" },
  raadiotr2: { name: "Müziking", icon: "bot-raadiotr2" },
};

/** Bot olmayan süreçlerin görünen adı; kimliği bilinmeyen olduğu gibi kalır. */
const OTHER: Record<string, string> = {
  lavalink: "Lavalink (ses sunucusu)",
  ytstream: "ytstream (YouTube akışı)",
  "gwrctl-server": "GWR Control sunucusu",
  "gwrctl-agent": "GWR Control agent'ı",
};
/** Ses zincirindeki süreçler dalga ikonu, geri kalanı sunucu ikonu alır. */
const AUDIO = new Set(["lavalink", "ytstream"]);

function processName(proc: string): string {
  return BOTS[proc]?.name ?? OTHER[proc] ?? proc;
}

export function BotPage(): ReactNode {
  const navigate = useNavigate();

  const pm2 = useLatestJobResult<Pm2Row[]>("bot.pm2.status");
  const diagnosis = useLatestJobResult<Diagnosis>("bot.diagnose");
  const state = useLatestJobResult<BotState>("bot.state");

  // bot.state tek bir süreci raporluyor; eylem aynı sürece gitmeli.
  const playerProcess = state.data?.bot === "raadiotr2" ? "raadiotr2" : "raadiotr";

  return (
    <>
      <PageHeader
        kicker="sistem"
        title="Discord botları"
        actions={
          <>
            <Button icon={RotateCw} loading={pm2.refreshing} onClick={() => void pm2.refresh()}>
              Durumu yenile
            </Button>
            <Button
              variant="primary"
              icon={Stethoscope}
              loading={diagnosis.refreshing}
              onClick={() => void diagnosis.refresh()}
            >
              {diagnosis.refreshing ? "Teşhis sürüyor…" : "Botu teşhis et · ~20 sn"}
            </Button>
          </>
        }
      />

      <PageBody>
        {diagnosis.data && !diagnosis.data.healthy && (
          <Banner kicker="teşhis">
            İlk kopan halka: <strong>{diagnosis.data.firstFailure}</strong>
            {diagnosis.data.suggestedFix && ` — önerilen: ${diagnosis.data.suggestedFix}`}
          </Banner>
        )}

        {[pm2.error, diagnosis.error, state.error]
          .filter((e): e is string => Boolean(e))
          .map((e) => (
            <Banner key={e} tone="danger">
              {e}
            </Banner>
          ))}

        <Card title="pm2 süreçleri" hint={age(pm2.job)} flush>
          {pm2.loadError ? (
            <ErrorState error={pm2.loadError} onRetry={pm2.reload} />
          ) : pm2.data?.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3">
              {pm2.data.map((proc) => {
                const online = proc.status === "online";
                // Kısa uptime + çok restart = crash loop; "online" yeşili yanıltmasın.
                const looping = online && proc.restarts > 5 && proc.uptimeMin < 10;
                const tone: Tone = !online ? "danger" : looping ? "warn" : "ok";
                return (
                  <div
                    key={proc.name}
                    className="grid min-w-0 gap-2 border-b border-neutral-900 px-4 py-3.5 sm:border-r"
                  >
                    <div className="flex items-center gap-2.5">
                      {BOTS[proc.name] ? (
                        <AppIcon id={BOTS[proc.name]!.icon} name={BOTS[proc.name]!.name} size={30} />
                      ) : (
                        <span className="grid size-[30px] shrink-0 place-items-center rounded-[7px] bg-surface-2 text-neutral-400">
                          {AUDIO.has(proc.name) ? <AudioLines size={16} aria-hidden /> : <Server size={16} aria-hidden />}
                        </span>
                      )}
                      <span className="grid min-w-0 gap-0.5">
                        <span className="flex items-center gap-1.5 truncate text-sm font-medium text-neutral-100">
                          <Dot tone={tone} />
                          {processName(proc.name)}
                        </span>
                        <span className="mono text-2xs text-neutral-600">{proc.name}</span>
                      </span>
                      <span className="ml-auto text-2xs" style={{ color: toneColor[tone] }}>
                        {looping ? "crash loop?" : proc.status}
                      </span>
                    </div>
                    <span className="text-2xs text-neutral-500">
                      {proc.memoryMb} MB · cpu %{proc.cpu} · {proc.uptimeMin} dk · {proc.restarts} restart
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {/* Canlı log süreç işi: başlar başlamaz iş sayfasına gidilir, akış orada. */}
                      <JobAction
                        type="bot.pm2.logs"
                        params={{ process: proc.name, errorsOnly: false }}
                        label={`${processName(proc.name)} logu`}
                        variant="ghost"
                        icon={ScrollText}
                        wait={false}
                        quiet
                        onDone={(job) => navigate(`/jobs/${job.id}`)}
                      >
                        Log
                      </JobAction>
                      <JobAction
                        type="bot.pm2.restart"
                        params={{ process: proc.name }}
                        label={`${processName(proc.name)} yeniden başlatma`}
                        icon={RotateCw}
                        onDone={() => pm2.refresh()}
                      >
                        Yeniden başlat
                      </JobAction>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <Empty>
              {pm2.isLoading
                ? "Yükleniyor…"
                : pm2.data
                  ? "pm2'de süreç görünmüyor"
                  : "Henüz durum sorgusu çalışmadı — “Durumu yenile” ile başlat."}
            </Empty>
          )}
        </Card>

        <Card
          title="Canlı oynatıcılar"
          hint={
            [state.data?.bot ? `${processName(state.data.bot)} raporluyor` : null, age(state.job) ?? "gerçek dinleyiciler"]
              .filter(Boolean)
              .join(" · ")
          }
          action={
            <>
              <JobAction
                type="bot.player.stopAll"
                params={{ process: "both" }}
                label="Tüm oynatıcıları durdurma"
                variant="danger"
                icon={OctagonX}
                onDone={() => state.refresh()}
              >
                ACİL: tümünü durdur
              </JobAction>
              <Button size="sm" icon={RotateCw} loading={state.refreshing} onClick={() => void state.refresh()}>
                Yenile
              </Button>
            </>
          }
          flush
        >
          {state.loadError ? (
            <ErrorState error={state.loadError} onRetry={state.reload} />
          ) : state.data?.guilds.length ? (
            state.data.guilds.map((guild) => (
              <div key={guild.id} className="row flex-wrap gap-x-3.5 gap-y-2">
                <div className="grid min-w-0 flex-1 basis-[220px] gap-0.5">
                  <span className="truncate text-neutral-100">{guild.name}</span>
                  <span className="truncate text-xs text-neutral-400">
                    {guild.playing ?? "—"}
                    {guild.source ? ` · ${guild.source}` : ""}
                  </span>
                  <span className="text-2xs text-neutral-600">
                    {guild.listeners ?? 0} dinleyici · {guild.queueLength ?? 0} kuyrukta
                    {guild.paused ? " · duraklatıldı" : ""}
                  </span>
                </div>
                <div className="ml-auto flex flex-wrap gap-1.5">
                  <JobAction
                    type="bot.player.action"
                    params={{ process: playerProcess, guildId: guild.id, action: guild.paused ? "resume" : "pause" }}
                    label={`${guild.name}: ${guild.paused ? "devam" : "duraklat"}`}
                    variant="ghost"
                    onDone={() => state.refresh()}
                  >
                    {guild.paused ? "Devam" : "Duraklat"}
                  </JobAction>
                  <JobAction
                    type="bot.player.action"
                    params={{ process: playerProcess, guildId: guild.id, action: "skip" }}
                    label={`${guild.name}: atla`}
                    variant="ghost"
                    onDone={() => state.refresh()}
                  >
                    Atla
                  </JobAction>
                  <JobAction
                    type="bot.player.action"
                    params={{ process: playerProcess, guildId: guild.id, action: "stop" }}
                    label={`${guild.name}: durdur`}
                    variant="danger"
                    onDone={() => state.refresh()}
                  >
                    Durdur
                  </JobAction>
                </div>
              </div>
            ))
          ) : (
            <Empty>
              {state.isLoading
                ? "Yükleniyor…"
                : state.data
                  ? "Hiçbir sunucuda aktif oynatma yok"
                  : "Oynatıcı durumu henüz okunmadı — “Yenile” ile sorgula."}
            </Empty>
          )}
        </Card>

        <Card
          title="Teşhis zinciri"
          hint={age(diagnosis.job) ?? "pm2 → zapret → gateway → Lavalink → bot /health"}
          flush
        >
          {diagnosis.loadError ? (
            <ErrorState error={diagnosis.loadError} onRetry={diagnosis.reload} />
          ) : diagnosis.data?.findings.length ? (
            diagnosis.data.findings.map((finding) => (
              <div key={finding.step} className="row flex-wrap gap-y-1 py-2.5">
                <Dot tone={finding.ok ? "ok" : "danger"} />
                <span className="mono text-xs text-neutral-200 lg:min-w-[220px]">{finding.step}</span>
                <span className="min-w-0 basis-full text-2xs break-words text-neutral-500 lg:basis-auto lg:flex-1">
                  {finding.detail}
                </span>
              </div>
            ))
          ) : (
            <Empty>{diagnosis.isLoading ? "Yükleniyor…" : "Henüz teşhis çalışmadı"}</Empty>
          )}
        </Card>

        <Card title="Radyo istasyonları" flush>
          <div className="flex flex-wrap items-center gap-3 p-4">
            <div className="grid min-w-0 flex-1 basis-[220px] gap-0.5">
              <span className="text-sm text-neutral-200">Yayın sağlığı taraması</span>
              <span className="text-2xs text-neutral-600">İstasyon sayısına göre 2-10 dk sürer; iş sayfasında izlenir.</span>
            </div>
            <JobAction
              type="bot.stations.healthScan"
              params={{ timeoutMs: 8000 }}
              label="İstasyon taraması"
              size="md"
              wait={false}
              quiet
              onDone={(job) => navigate(`/jobs/${job.id}`)}
            >
              Taramayı başlat
            </JobAction>
          </div>
        </Card>
      </PageBody>
    </>
  );
}
